import { useCallback } from 'react';
import { sendMessage as apiSendMessage } from '../../services/sessionService';
import { updateChatSessionContext } from '../../services/chatSessionApi';
const parseTimestamp = (timestamp) => {
    if (!timestamp) return new Date(0);
    try {
        const date = new Date(timestamp);
        return isNaN(date.getTime()) ? new Date(0) : date;
    } catch (e) {
        return new Date(0);
    }
};
export const useSessionActions = ({
    sessionId,
    isConnected,
    sessionContext,
    setSessionId,
    setSessionContext,
    setIsConnected,
    setTimelineItems,
    setProcessingStatus,
    setIsSending,
    setHasAuthFailed,
    setReconnectPendingOnShutdown,
    setIsReconnecting,
    setReconnectAttempts,
    setTeamName,
    setActiveWidgets,
    messageIdTrackersRef,
    isMounted,
    processedItemIds,
    reconnectTimerRef,
    initializeSessionRef,
    initialSessionEstablished,
    addMessage,
    removeMessage,
    resetSessionInitialization,
    setIsSubscriptionRequired,
    defaultSessionOptions,
}) => {
    const addTimelineItem = useCallback((item) => {
        if (!isMounted.current) return;
        if (!item.timestamp) item.timestamp = new Date().toISOString();
        if (!item.id) item.id = `${item.type || 'event'}-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
        if (processedItemIds.current.has(item.id) && item.id !== 'stt-in-progress') {
            return;
        }
        setTimelineItems(prevItems => {
            const filteredPrev = prevItems.filter(p => p.id !== item.id);
            if (item.source === 'User' && item.type === 'message' && !item.isTemporary) {
                const withoutPlaceholder = filteredPrev.filter(p => p.id !== 'stt-in-progress');
                processedItemIds.current.add(item.id);
                return [...withoutPlaceholder, item];
            }
            if (item.source === 'User' && item.type === 'message') {
                const lastItem = filteredPrev[filteredPrev.length - 1];
                if (lastItem && lastItem.source === 'User' && lastItem.type === 'message') {
                    const timeDiff = Math.abs(parseTimestamp(item.timestamp) - parseTimestamp(lastItem.timestamp));
                    if (item.content === lastItem.content && timeDiff < 2000) {
                        console.error(`[useSessionWithAuth] Duplicate user message detected and filtered. Content: "${item.content}", Time diff: ${timeDiff}ms`);
                        return prevItems;
                    }
                }
            }
            processedItemIds.current.add(item.id);
            return [...filteredPrev, item];
        });
    }, [isMounted, processedItemIds, setTimelineItems]);
    const resetAuthState = useCallback(() => {
        if (!isMounted.current) return;
        removeMessage('auth-error');
        setHasAuthFailed(false);
    }, [removeMessage, setHasAuthFailed, isMounted]);
    const resetState = useCallback((startNew = true, options = {}) => {
        if (!isMounted.current) return;
        console.log(`[resetState] Resetting state. Start new session: ${startNew}`);
        setSessionId(null);
        setSessionContext([]);
        setIsConnected(false);
        setTimelineItems([]);
        setProcessingStatus('idle');
        setIsSending(false);
        setHasAuthFailed(false);
        setTeamName(null);
        setActiveWidgets([]);
        if (messageIdTrackersRef) {
            messageIdTrackersRef.current = {};
        }
        removeMessage('auth-error');
        removeMessage('reconnect-notice');
        removeMessage('reconnect-error');
        setReconnectPendingOnShutdown(false);
        setIsReconnecting(false);
        setReconnectAttempts(0);
        if (reconnectTimerRef.current) {
            clearTimeout(reconnectTimerRef.current);
        }
        processedItemIds.current.clear();
        resetSessionInitialization();
        setIsSubscriptionRequired(false);
        if (startNew) {
            console.log("Reset state is requesting a new session.");
            initialSessionEstablished.current = false;
            initializeSessionRef.current(null, { ...defaultSessionOptions, ...options });
        }
    }, [
        isMounted, resetSessionInitialization, setIsSubscriptionRequired, removeMessage, defaultSessionOptions,
        setSessionId, setSessionContext, setIsConnected, setTimelineItems, setProcessingStatus,
        setIsSending, setHasAuthFailed, setReconnectPendingOnShutdown, setIsReconnecting,
        setReconnectAttempts, setTeamName, setActiveWidgets, reconnectTimerRef, processedItemIds, initialSessionEstablished, initializeSessionRef, messageIdTrackersRef
    ]);
    const addResourceToContext = useCallback(async (id, type, sessionIdOverride = null) => {
        const targetSessionId = sessionIdOverride || sessionId;
        if (!targetSessionId) {
            console.error("Cannot add resource to context: No active session.");
            return;
        }
        const currentContext = [...sessionContext];
        const alreadyExists = currentContext.some(res => res.id === id && res.type === type);
        if (alreadyExists) {
            console.log(`Resource ${type}:${id} already in context. Skipping.`);
            return;
        }
        const newResource = { id, type };
        const newContext = [...currentContext, newResource];
        if (!sessionIdOverride) {
            setSessionContext(newContext);
        }
        try {
            await updateChatSessionContext(targetSessionId, newContext);
            console.log(`Successfully added resource ${type}:${id} to session ${targetSessionId} context.`);
            if (sessionIdOverride && sessionIdOverride === sessionId) {
                setSessionContext(newContext);
            }
        } catch (error) {
            console.error(`Failed to add resource ${type}:${id} to context on backend:`, error);
            if (!sessionIdOverride) {
                setSessionContext(currentContext);
            }
            addMessage({ id: 'context-update-error', text: `Failed to add resource to chat: ${error.message}`, severity: 'error' });
        }
    }, [sessionId, sessionContext, addMessage, setSessionContext]);
    const removeResourceFromContext = useCallback(async (id, type, sessionIdOverride = null) => {
        const targetSessionId = sessionIdOverride || sessionId;
        if (!targetSessionId) {
            console.error("Cannot remove resource from context: No active session.");
            return;
        }
        const currentContext = [...sessionContext];
        const resourceExists = currentContext.some(res => res.id === id && res.type === type);
        if (!resourceExists) {
            console.log(`Resource ${type}:${id} not in context. Skipping removal.`);
            return;
        }
        const newContext = currentContext.filter(res => !(res.id === id && res.type === type));
        if (!sessionIdOverride) {
            setSessionContext(newContext);
        }
        try {
            await updateChatSessionContext(targetSessionId, newContext);
            console.log(`Successfully removed resource ${type}:${id} from session ${targetSessionId} context.`);
            if (sessionIdOverride && sessionIdOverride === sessionId) {
                setSessionContext(newContext);
            }
        } catch (error) {
            console.error(`Failed to remove resource ${type}:${id} from context on backend:`, error);
            if (!sessionIdOverride) {
                setSessionContext(currentContext);
            }
            addMessage({ id: 'context-update-error', text: `Failed to remove resource from chat: ${error.message}`, severity: 'error' });
        }
    }, [sessionId, sessionContext, addMessage, setSessionContext]);
    const sendMessage = useCallback(async (finalMessageContent) => {
        if (!sessionId || !finalMessageContent?.trim() || !isConnected || !isMounted.current) {
            console.warn("Send message skipped:", { sessionId, content: finalMessageContent?.substring(0, 20) + '...', isConnected });
            return false;
        }
        setIsSending(true);
        setProcessingStatus('processing');
        try {
            await apiSendMessage(sessionId, finalMessageContent);
            return true;
        } catch (err) {
            console.error('Error sending message:', err);
            if (isMounted.current) {
                setProcessingStatus('error');
                if (err.status === 429) {
                     addMessage({ id: 'send-limit', text: "Daily usage limit exceeded. Message not sent.", severity: 'warning' });
                } else if (err.message && (err.message.includes('Session not found') || err.message.includes('session not found'))) {
                    addMessage({
                        id: 'send-error',
                        text: 'Session expired or not found.',
                        severity: 'error',
                        action: {
                            label: 'Start New Session',
                            onClick: () => {
                                removeMessage('send-error');
                                resetState(true);
                            }
                        }
                    });
                } else {
                    addMessage({ id: 'send-error', text: `Error sending message: ${err.message}`, severity: 'error' });
                }
            }
            return false;
        } finally {
            if (isMounted.current) {
                setIsSending(false);
            }
        }
    }, [sessionId, isConnected, addMessage, setIsSending, setProcessingStatus, isMounted, resetState, removeMessage]);
    const toggleWidget = useCallback((widgetId) => {
        setActiveWidgets(prev => {
            if (prev.includes(widgetId)) {
                return prev.filter(id => id !== widgetId);
            }
            return [...prev, widgetId];
        });
    }, [setActiveWidgets]);
    const openWidget = useCallback((widgetId) => {
        setActiveWidgets(prev => {
            if (prev.includes(widgetId)) return prev;
            return [...prev, widgetId];
        });
    }, [setActiveWidgets]);
    const closeWidget = useCallback((widgetId) => {
        setActiveWidgets(prev => prev.filter(id => id !== widgetId));
    }, [setActiveWidgets]);
    return {
        addTimelineItem,
        resetAuthState,
        resetState,
        addResourceToContext,
        removeResourceFromContext,
        sendMessage,
        toggleWidget,
        openWidget,
        closeWidget,
    };
};